import { useState, useEffect } from "react";

// Compass-themed loading spinner with sunflower accents
export function LoadingSpinner({ size = "md", message = "Loading..." }) {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
    xl: "w-24 h-24"
  };

  return (
    <div className="flex flex-col items-center justify-center gap-4" data-testid="loading-spinner">
      <div className={`relative ${sizeClasses[size]}`}>
        {/* Outer ring - compass rose */}
        <div className="absolute inset-0 rounded-full border-2 border-[#d4e4d4]" />
        
        {/* Compass needle - rotating */}
        <div className="absolute inset-0 flex items-center justify-center animate-spin" style={{ animationDuration: "2s" }}>
          <svg viewBox="0 0 24 24" className="w-full h-full text-[#7a9e7a]">
            {/* North needle (gold/primary) */}
            <polygon 
              points="12,2 14,12 12,10 10,12" 
              fill="#a89060" 
              className="drop-shadow-sm"
            />
            {/* South needle (darker) */}
            <polygon 
              points="12,22 14,12 12,14 10,12" 
              fill="#5a8a5a" 
              className="drop-shadow-sm"
            />
          </svg>
        </div>
        
        {/* Center dot */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-[#a89060] shadow-sm" />
        </div>
        
        {/* Cardinal points */}
        <div className="absolute inset-0">
          <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 text-[8px] font-bold text-[#5a8a5a]">N</span>
          <span className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1 text-[8px] font-bold text-[#a69373]/30">S</span>
          <span className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 text-[8px] font-bold text-[#a69373]/30">W</span>
          <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 text-[8px] font-bold text-[#a69373]/30">E</span>
        </div>
      </div>
      
      {message && (
        <p className="text-[#a69373]/60 text-sm animate-pulse">{message}</p>
      )}
    </div>
  );
}

// Full page loading screen with compass
export function PageLoader({ message = "Finding your path..." }) {
  const [dots, setDots] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? "" : prev + ".");
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 bg-background z-50 flex items-center justify-center" data-testid="page-loader">
      <div className="text-center">
        <LoadingSpinner size="xl" message="" />
        <p className="mt-6 text-[#a69373] font-heading text-lg">{message}{dots}</p>
        <p className="mt-2 text-[#a69373]/50 text-sm">Your journey awaits</p>
      </div>
    </div>
  );
}

// Inline loading indicator
export function InlineLoader({ className = "" }) {
  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <div className="w-4 h-4 border-2 border-[#d4e4d4] border-t-[#7a9e7a] rounded-full animate-spin" />
      <span className="text-[#a69373]/60 text-sm">Loading...</span>
    </div>
  );
}

// Skeleton loader for content
export function SkeletonCard({ className = "" }) {
  return (
    <div className={`glass-card animate-pulse ${className}`}>
      <div className="p-4 space-y-3">
        <div className="h-4 bg-[#d4e4d4] rounded w-3/4" />
        <div className="h-3 bg-[#d4e4d4] rounded w-1/2" />
        <div className="h-3 bg-[#d4e4d4] rounded w-2/3" />
      </div>
    </div>
  );
}

export default LoadingSpinner;
